<template>
  <div id="resultados-page">
    <breadcrumb :items="breadcrumbItems" />

    <main class="search-layout">
      <aside class="search-sidebar">
        <h2 class="sidebar-title">Filtrar por:</h2>

        <a
          href="#"
          class="filter-item"
          :class="{ active: filtroAtivo === 'GERAL' }"
          @click.prevent="mudarFiltro('GERAL')"
        >
          <div class="filter-label">
            <i class="fa-solid fa-globe"></i>
            <span>Geral</span>
          </div>
          <span class="filter-counter">{{ estatisticas["GERAL"] || 0 }}</span>
        </a>

        <a
          href="#"
          class="filter-item"
          :class="{ active: filtroAtivo === 'ATIVIDADE' }"
          @click.prevent="mudarFiltro('ATIVIDADE')"
        >
          <div class="filter-label">
            <i class="fa-solid fa-calendar-days"></i>
            <span>Atividades</span>
          </div>
          <span class="filter-counter">{{
            estatisticas["ATIVIDADE"] || 0
          }}</span>
        </a>

        <a
          href="#"
          class="filter-item"
          :class="{ active: filtroAtivo === 'ACAO' }"
          @click.prevent="mudarFiltro('ACAO')"
        >
          <div class="filter-label">
            <i class="fa-solid fa-briefcase"></i>
            <span>Ações</span>
          </div>
          <span class="filter-counter">{{ estatisticas["ACAO"] || 0 }}</span>
        </a>

        <a
          href="#"
          class="filter-item"
          :class="{ active: filtroAtivo === 'VAGA' }"
          @click.prevent="mudarFiltro('VAGA')"
        >
          <div class="filter-label">
            <i class="fa-solid fa-user-graduate"></i>
            <span>Vagas</span>
          </div>
          <span class="filter-counter">{{ estatisticas["VAGA"] || 0 }}</span>
        </a>

        <a
          href="#"
          class="filter-item"
          :class="{ active: filtroAtivo === 'NOTICIA' }"
          @click.prevent="mudarFiltro('NOTICIA')"
        >
          <div class="filter-label">
            <i class="fa-solid fa-newspaper"></i>
            <span>Notícias</span>
          </div>
          <span class="filter-counter">{{ estatisticas["NOTICIA"] || 0 }}</span>
        </a>
      </aside>

      <section
        class="search-results-content"
        ref="resultadosContainer"
      >
        <h1
          class="results-header"
          v-if="!isLoading"
        >
          {{ estatisticas[filtroAtivo] || 0 }} resultados encontrados para "{{
            termoBusca
          }}"
        </h1>
        <h1
          class="results-header"
          v-else
        >
          Buscando resultados...
        </h1>

        <div
          v-if="isLoading"
          class="loading-state"
        >
          <i class="fa-solid fa-circle-notch fa-spin"></i> Carregando...
        </div>

        <div
          v-else-if="resultados.length === 0"
          class="empty-state"
        >
          Não encontramos nenhum resultado para o termo "{{ termoBusca }}".
        </div>

        <article
          class="result-card"
          v-for="item in resultados"
          :key="item.id"
          @click="abrirResultado(item)"
        >
          <div class="capa">
            <img
              class="imagem"
              src="@/assets/logoGex.jpg"
              alt="capa"
            />
          </div>
          <div class="result-container">
            <div class="result-title-wrapper">
              <h2
                class="result-card-title"
                v-html="destacarTermo(item.title)"
              ></h2>
              <span class="type-badge">{{ formatarTipo(item.type) }}</span>
            </div>
            <p
              class="result-card-description"
              v-html="destacarTermo(item.description)"
            ></p>
          </div>
        </article>

        <div
          class="pagination-controls"
          v-if="totalPages > 1 && !isLoading"
        >
          <button
            :disabled="currentPage === 0"
            @click="mudarPagina(currentPage - 1)"
            class="btn-page"
          >
            Anterior
          </button>
          <span class="page-info"
            >Página {{ currentPage + 1 }} de {{ totalPages }}</span
          >
          <button
            :disabled="!hasMore"
            @click="mudarPagina(currentPage + 1)"
            class="btn-page"
          >
            Próxima
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import buscaGeralService from "@/service/buscaGeralService";
import Breadcrumb from "@/components/common/Breadcrumb.vue";

const resultadosContainer = ref(null);

const route = useRoute();
const router = useRouter();

const termoBusca = ref(route.query.q || "");
const filtroAtivo = ref(route.query.filtro || "GERAL");
const resultados = ref([]);
const estatisticas = ref({});
const isLoading = ref(true);

const currentPage = ref(0);
const totalPages = ref(1);
const hasMore = ref(false);

const breadcrumbItems = computed(() => {
  return [
    { label: "Início", to: "/" },
    { label: "Resultados", to: "/resultados" },
  ];
});

onMounted(() => {
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    if (resultadosContainer.value) {
      resultadosContainer.value.scrollTop = 0;
    }
  });

  if (termoBusca.value) {
    carregarResultados();
  } else {
    isLoading.value = false;
  }
});

watch(
  () => [route.query.q, route.query.filtro],
  ([novoTermo, novoFiltro]) => {
    termoBusca.value = novoTermo || "";
    filtroAtivo.value = novoFiltro || "GERAL";

    currentPage.value = 0;

    carregarResultados();
  }
);

async function carregarResultados() {
  isLoading.value = true;
  try {
    const dados = await buscaGeralService.buscaGeral(
      termoBusca.value,
      filtroAtivo.value,
      currentPage.value,
      6
    );

    const rawItems = dados.items || [];
    const uniqueKeys = new Set();
    resultados.value = rawItems.filter((item) => {
      const key = `${item.type}-${item.id}`;
      if (uniqueKeys.has(key)) {
        return false;
      }
      uniqueKeys.add(key);
      return true;
    });
    estatisticas.value = dados.stats || {};
    currentPage.value = dados.currentPage || 0;
    totalPages.value = dados.totalPages || 1;
    hasMore.value = dados.hasMore || false;
  } catch (error) {
    console.error(error);
    resultados.value = [];
  } finally {
    isLoading.value = false;
  }
}

function mudarFiltro(novoFiltro) {
  if (filtroAtivo.value === novoFiltro) return;

  filtroAtivo.value = novoFiltro;
  currentPage.value = 0;

  router.replace({ query: { ...route.query, filtro: novoFiltro } });

  carregarResultados();
}

async function mudarPagina(novaPagina) {
  currentPage.value = novaPagina;

  await carregarResultados();

  await nextTick();

  window.scrollTo({ top: 0, behavior: "smooth" });

  if (resultadosContainer.value) {
    resultadosContainer.value.scrollTop = 0;
  }
}

function formatarTipo(tipoBd) {
  const mapa = {
    ACAO: "Ação de Extensão",
    ATIVIDADE: "Atividade",
    NOTICIA: "Notícia",
    VAGA: "Vaga",
    GERAL: "Geral",
  };
  return mapa[tipoBd] || tipoBd;
}

function destacarTermo(textoOriginal) {
  if (!textoOriginal) return "";
  if (!termoBusca.value) return textoOriginal;

  const termoEscapado = termoBusca.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${termoEscapado})`, "gi");

  return textoOriginal.replace(regex, "<mark>$1</mark>");
}
function abrirResultado(item) {
  const rotasPorTipo = {
    ACAO: `/acoes/${item.id}`,
    ATIVIDADE: `/atividades/${item.id}`,
    NOTICIA: `/noticias/${item.id}`,
    PARCERIA: `/parcerias/${item.id}`,
    VAGA: `/Vaga/${item.id}`,
  };

  const rota = rotasPorTipo[item.type];

  if (!rota) {
    console.warn("Tipo não mapeado:", item.type);
    return;
  }

  router.push(rota);
}
</script>

<style scoped>
#resultados-page {
  min-height: calc(100vh - 130px);
}
.search-layout {
  display: flex;
  flex-direction: column;
  color: #1f2328;
  font-size: 14px;
  overflow: hidden;
}

.search-sidebar {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 2rem;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2328;
}

.filter-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  color: #1f2328;
  border-left: 4px solid transparent;
  gap: 1rem;

  text-decoration: none;
  font-size: 14px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.filter-item.active {
  background-color: #e2e2e7;
  border-left-color: #3b1f8c;
}

.filter-item:hover:not(.active) {
  background-color: #f3f4f6;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-label i {
  color: #656d76;
  width: 16px;
  text-align: center;
}

.filter-counter {
  background-color: #eff1f3;
  color: #57606a;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 2em;
  min-width: 20px;
  text-align: center;
}

.search-results-content {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
  padding-right: 16px;
}

.search-results-content::-webkit-scrollbar,
.search-sidebar::-webkit-scrollbar {
  width: 6px;
}

.search-results-content::-webkit-scrollbar-track,
.search-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.search-results-content::-webkit-scrollbar-thumb,
.search-sidebar::-webkit-scrollbar-thumb {
  background-color: #d0d7de;
  border-radius: 10px;
}

.search-results-content::-webkit-scrollbar-thumb:hover {
  background-color: #8c959f;
}

.results-header {
  font-size: 20px;
  font-weight: 600;
  padding-bottom: 16px;
  border-bottom: 1px solid #d0d7de;
  margin-bottom: 16px;
}

.loading-state,
.empty-state {
  padding: 40px 0;
  text-align: center;
  color: #656d76;
  font-size: 16px;
}

.result-card {
  padding: 16px;
  border-bottom: 1px solid #d8dee4;
  display: flex;
  flex-direction: row;
  gap: 2rem;
  cursor: pointer;

  transition: background-color 0.3s ease-in-out;
}
.result-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.imagem {
  width: 200px;
  height: 150px;
  border-radius: 0.4rem;
}

.result-card:hover {
  background-color: #e2e2e7;
  border-radius: 0.4rem;
}

.result-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.5rem;
}

.result-card-title {
  font-size: 1.4rem;
  margin-bottom: 0rem;
  font-weight: 600;
  color: #3b1f8c;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.result-card-title:hover {
  text-decoration: underline;
}

.type-badge {
  font-size: 12px;
  background-color: #ece5ff;
  color: #563578;
  padding: 0 7px;
  border-radius: 2em;
  font-weight: 500;
}

.result-card-description {
  font-size: 1rem;
  color: #57606a;
  line-height: 1.4;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;

  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 32px 0;
}

.btn-page {
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #3b1f8c;
  background-color: transparent;
  border: 2px solid #3b1f8c;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  background-color: #3b1f8c;
  border-color: #3b1f8c;
  color: white;
}

.btn-page:disabled {
  color: #8c959f;
  background-color: #f6f8fa;
  border: 2px solid #d0d7de;

  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #57606a;
}

:deep(mark) {
  background-color: #fff8c5;
  color: #1f2328;
  font-weight: 600;
  padding: 0 2px;
  border-radius: 2px;
}

@media (max-width: 768px) {
  .search-sidebar {
    flex-wrap: wrap;
    gap: 1rem;
  }

  .filter-item {
    flex-shrink: 0;
    border-left: none;
    border-bottom: 3px solid transparent;
  }
  .filter-item.active {
    border-left: none;
    border-bottom-color: #41008b;
  }
}
</style>
